import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import PencilIcon from '../../../../../generic/icons/IconLib/PencilIcon';
import { ImageViewer } from '../../../../../generic/ui/ImageViewer';
import { IUploadedImage } from '../../../../../modules/API/Images/models/imageUploadResult';

/** Столько плиток в ряд и столько же заглушек на время загрузки. */
const COLUMNS = 3;

export interface OrderImagesBlockProps {
    images: IUploadedImage[];
    comment: string;
    isLoading: boolean;
    /** Комментарий над гридом, а не под ним. */
    commentFirst?: boolean;
    /** Плитки фиксированного размера на десктопе, а не на всю ширину. */
    compact?: boolean;
}

const OrderImagesBlock: React.FC<OrderImagesBlockProps> = ({
    images,
    comment,
    isLoading,
    commentFirst = false,
    compact = false,
}) => {
    const [expanded, setExpanded] = useState<IUploadedImage | null>(null);
    const trimmedComment = comment.trim();

    if (isLoading) {
        return (
            <Block>
                <Grid $compact={compact}>
                    {Array.from({ length: COLUMNS }).map((_, index) => (
                        <SkeletonTile key={index} />
                    ))}
                </Grid>
                <SkeletonComment />
            </Block>
        );
    }

    // Нечего показывать — блок не занимает места вовсе.
    if (images.length === 0 && !trimmedComment) return null;

    const commentRow = trimmedComment && (
        <CommentRow>
            <PencilIcon size={13} />
            <CommentText>{trimmedComment}</CommentText>
        </CommentRow>
    );

    return (
        <Block>
            {commentFirst && commentRow}

            {images.length > 0 && (
                <Grid $compact={compact}>
                    {images.map((image) => (
                        <Thumb
                            key={image.RecID}
                            type='button'
                            onClick={() => setExpanded(image)}
                            aria-label={image.FileName}
                            title={image.FileName}
                        >
                            <ThumbImage src={image.FilePath} alt={image.FileName} loading='lazy' />
                        </Thumb>
                    ))}
                </Grid>
            )}

            {!commentFirst && commentRow}

            {expanded && (
                <ImageViewer
                    src={expanded.FilePath}
                    alt={expanded.FileName}
                    onClose={() => setExpanded(null)}
                />
            )}
        </Block>
    );
};

export default OrderImagesBlock;

const shimmer = keyframes`
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const shimmerBackground = css`
    background-size: 400% 100%;
    animation: ${shimmer} 1.4s ease infinite;
`;

const Block = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

/** Размер плитки в compact-режиме — примерно как на мобиле. */
const COMPACT_TILE = '7rem';

const Grid = styled.div<{ $compact: boolean }>`
    display: grid;
    grid-template-columns: ${({ $compact }) =>
        $compact
            ? `repeat(auto-fill, ${COMPACT_TILE})`
            : `repeat(${COLUMNS}, minmax(0, 1fr))`};
    gap: 0.375rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        grid-template-columns: repeat(${COLUMNS}, minmax(0, 1fr));
    }
`;

const tileShape = `
  width: 100%;
  aspect-ratio: 1;
  border-radius: 0.75rem;
  overflow: hidden;
`;

const Thumb = styled.button`
    ${tileShape}
    padding: 0;
    border: 1px solid ${({ theme }) => theme.colors.line};
    background: ${({ theme }) => theme.colors.line2};
    cursor: pointer;
    transition: border-color ${({ theme }) => theme.motion.fast} ease;

    &:hover {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }
`;

const ThumbImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const SkeletonTile = styled.span`
    ${tileShape}
    display: block;
    background: linear-gradient(
        90deg,
        ${({ theme }) => theme.colors.line2} 25%,
        ${({ theme }) => theme.colors.line} 37%,
        ${({ theme }) => theme.colors.line2} 63%
    );
    ${shimmerBackground}
`;

const SkeletonComment = styled.span`
    display: block;
    height: 0.875rem;
    width: 60%;
    border-radius: ${({ theme }) => theme.radii.pill};
    background: linear-gradient(
        90deg,
        ${({ theme }) => theme.colors.line2} 25%,
        ${({ theme }) => theme.colors.line} 37%,
        ${({ theme }) => theme.colors.line2} 63%
    );
    ${shimmerBackground}
`;

const CommentRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    color: ${({ theme }) => theme.colors.ink3};
`;

const CommentText = styled.p`
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    line-height: 1.35;
    color: ${({ theme }) => theme.colors.ink2};
    white-space: pre-wrap;
    overflow-wrap: anywhere;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.md};
    }
`;
